import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Radio, RadioGroup, Stack } from '@chakra-ui/react';

import { transformVariantToMultiSelectOption } from './useAddons';
import { AddQuantityButton } from '../../components/ProductCard/ProductCard';
import MultiSelectDropdown, {
  useMultiSelectDropdown,
} from '../../components/MultiSelectDropdown';
import Button from '../../components/Button';
import { Paragraph } from '../../components/Typography';
import { getVariantFromSelectedOptionsLiquid } from '../utils';
import { useGlobalContext } from '../../subscriptions/state/GlobalContext';
import {
  FEATURE_FLAGS,
  isFeatureFlagEnabled,
} from '../../../widgetSrc/utils/featureFlags';

interface Props {
  slidePanelAddon: GlobalProduct & { addonGroupId?: string };
  selectedVariant: GlobalVariant | null;
  onSelect: (selectedAddon: { id: string; quantity: number }) => void;
  onConfirm: (selectedOption?: MultiSelectOption) => void;
}

const AddonSliderSelector = ({
  slidePanelAddon,
  selectedVariant,
  onSelect,
  onConfirm,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const {
    increase,
    decrease,
    handleSelect,
    handleConfirm,
    selected,
    isOpen,
    options,
    quantity,
    setIsOpen,
    handleOnOptionClicked,
    handleClose,
    handleOnRadioChange,
    addonSellingPolicy,
  } = useAddonSliderSelector(
    slidePanelAddon,
    selectedVariant,
    onSelect,
    onConfirm,
  );
  const { ignoreBaBOutOfStockAllowance } = useGlobalContext();
  const isBaBIgnoreOutOfStockEnabled = isFeatureFlagEnabled(
    FEATURE_FLAGS.LAUNCH_BAB_IGNORE_OUT_OF_STOCK,
  );

  const sellingPolicy = slidePanelAddon.addonSellingPolicy || 'SUBSCRIPTION';
  let sellingPolicyHelpText = '';
  const ignoreOutOfStock =
    isBaBIgnoreOutOfStockEnabled &&
    ignoreBaBOutOfStockAllowance &&
    selectedVariant.inventory &&
    selectedVariant.inventory?.inventoryPolicy === 'continue' &&
    selectedVariant.inventory?.inventoryManagement === 'shopify' &&
    selectedVariant.inventory?.inventoryQuantity <= 0;

  switch (sellingPolicy) {
    case 'ONETIME':
      sellingPolicyHelpText = t('addons.help.one_time');
      break;
    case 'SUBSCRIPTION':
      sellingPolicyHelpText = t('addons.help.subscription');
      break;
  }

  return (
    <SliderFooter>
      {!slidePanelAddon.hasOnlyDefaultVariant ? (
        <MultiSelectDropdown
          isOpen={isOpen}
          options={options}
          onClose={handleClose}
          onOptionClicked={handleOnOptionClicked}
          buttonText={t('addons.save')}
          onSelectClick={() => setIsOpen(!isOpen)}
          selectedOption={selected?.label}
          onConfirm={handleConfirm}
          label={t('addons.type')}
          helperText={
            !selected?.id ||
            !slidePanelAddon?.available ||
            !selectedVariant?.available ||
            ignoreOutOfStock
              ? t('addons.selection_unavailable')
              : ''
          }
          error={
            !slidePanelAddon?.available ||
            !selectedVariant?.available ||
            ignoreOutOfStock ||
            !selected?.id
          }
        />
      ) : (
        <Paragraph modifiers={['body']}> {selectedVariant?.name} </Paragraph>
      )}
      {sellingPolicy === 'ANY' && (
        <AddonsSellingPolicyPicker
          onRadioChange={handleOnRadioChange}
          addonsSellingPolicy={addonSellingPolicy}
        />
      )}
      <QuantitySelector>
        <AddQuantityButton
          style={{ width: '45%' }}
          onIncrease={increase}
          onDecrease={decrease}
          quantity={quantity}
          available={
            (slidePanelAddon?.available &&
              !!selectedVariant?.available &&
              !ignoreOutOfStock) ||
            true
          }
        />
        <Button
          style={{ marginLeft: '10%', width: '45%' }}
          onClick={handleSelect}
          filled
          fullWidth
          disabled={
            !selected?.id || !selectedVariant?.available || ignoreOutOfStock
          }
        >
          {t('addons.select')}
        </Button>
      </QuantitySelector>
      {sellingPolicyHelpText && (
        <PolicyHelpText>{sellingPolicyHelpText}</PolicyHelpText>
      )}
    </SliderFooter>
  );
};

const SliderFooter = styled.div.attrs({
  className: 'bundle-slider-footer',
})`
  width: 100%;
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  border-top: 1px solid #f1f2f3;
  background-color: inherit;
`;
const QuantitySelector = styled.div.attrs({
  className: 'bundle-quantity-selector',
})`
  width: 100%;
  margin-top: 20px;
  display: flex;
  justify-content: space-around;
  align-items: center;
`;
const PolicyHelpText = styled.div.attrs({
  className: 'policy-help-text',
})`
  font-size: 12px;
  margin-top: 16px;
`;

const useAddonSliderSelector = (
  slidePanelAddon: GlobalProduct & { addonGroupId?: string },
  selectedDefaultVariant: GlobalVariant | null,
  onSelect: (selectedAddon: {
    id: string;
    quantity: number;
    lineSellingPolicy: 'ONETIME' | 'SUBSCRIPTION';
    addonGroupId?: string;
  }) => void,
  onConfirm: (selectedOption: MultiSelectOption) => void,
) => {
  const selectedDefaultOption = selectedDefaultVariant?.selectedOptions
    ? transformVariantToMultiSelectOption(selectedDefaultVariant, true)
    : null;
  const [quantity, setQuantity] = useState<number>(1);
  const [selected, setSelected] = useState<MultiSelectOption | null>(
    selectedDefaultOption,
  );
  const [addonSellingPolicy, setAddonSellingPolicy] = useState<
    'ONETIME' | 'SUBSCRIPTION'
  >('ONETIME');

  const { isOpen, setIsOpen, handleOnOptionClicked, options } =
    useMultiSelectDropdown({
      initialOptions: slidePanelAddon?.optionsAndValues || [],
    });

  const handleOnRadioChange = (value) => {
    setAddonSellingPolicy(value);
  };

  const increase = () => setQuantity(quantity + 1);
  const decrease = () => setQuantity(Math.max(quantity - 1, 0));

  const handleConfirm = () => {
    const selectedObject = options.filter(({ isSelected }) => isSelected);
    const selectedVariant = getVariantFromSelectedOptionsLiquid(
      selectedObject,
      slidePanelAddon.variants,
    );

    if (selectedObject && selectedVariant) {
      const selectedVariantOption = {
        id: `${selectedVariant.id}`,
        label: selectedVariant.title,
      };
      setSelected(selectedVariantOption);
      onConfirm(selectedVariantOption);
      setIsOpen(false);
    }
  };

  const handleSelect = () => {
    onSelect({
      id: selected?.id as string,
      quantity,
      lineSellingPolicy: addonSellingPolicy,
      addonGroupId: slidePanelAddon.addonGroupId,
    });
  };

  const handleClose = () => {
    handleConfirm();
    setIsOpen(false);
  };

  return {
    increase,
    decrease,
    handleSelect,
    handleConfirm,
    selected,
    isOpen,
    setIsOpen,
    handleOnOptionClicked,
    options,
    quantity,
    handleClose,
    addonSellingPolicy,
    handleOnRadioChange,
  };
};

const AddonsSellingPolicyPicker = ({ onRadioChange, addonsSellingPolicy }) => {
  const { t } = useTranslation();

  return (
    <AddonsSellingpolicyPickerContainer>
      <RadioGroup onChange={onRadioChange} value={addonsSellingPolicy}>
        <Stack>
          <Radio _before={{ left: '3px', bottom: '7px' }} value="ONETIME">
            {t('addons.one_time')}
          </Radio>
          <Radio _before={{ left: '3px', bottom: '7px' }} value="SUBSCRIPTION">
            {t('addons.subscription')}
          </Radio>
        </Stack>
      </RadioGroup>
    </AddonsSellingpolicyPickerContainer>
  );
};

const AddonsSellingpolicyPickerContainer = styled.div.attrs({
  className: 'awt-selling-policy-container',
})`
  display: flex;
  justify-content: flex-start;
  width: 100%;
  padding: 20px 6px 0px 6px;

  .chakra-radio {
    position: relative;

    .chakra-radio__control::before {
      position: absolute;
      top: 8px;
      left: 4px;
    }
  }
`;

export default AddonSliderSelector;
